import { ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import z from "zod";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

// Accepts the object as given, or the same keys all absent.
export const zodObjectOrUndefined = <Shape extends z.ZodRawShape>(
  schema: z.ZodObject<Shape>,
) =>
  schema.or(
    z.object(
      Object.fromEntries(
        Object.keys(schema.shape).map((k) => [k, z.undefined().optional()]),
      ) as { [k in keyof Shape]: z.ZodOptional<z.ZodUndefined> },
    ),
  );

// Accepts a pending value so async content can still be copied on iOS, where
// clipboard writes must start inside the user gesture. navigator.clipboard is
// missing in some in-app webviews; fall back to execCommand.
export async function copyText(text: string | Promise<string>) {
  if (typeof text !== "string" && window.ClipboardItem) {
    try {
      await navigator.clipboard.write([
        new ClipboardItem({
          "text/plain": text.then((t) => new Blob([t], { type: "text/plain" })),
        }),
      ]);
      return;
    } catch (_) {
      // fall through
    }
  }
  const value = await text;
  try {
    await navigator.clipboard.writeText(value);
    return;
  } catch (_) {
    // fall through
  }
  const textarea = document.createElement("textarea");
  textarea.value = value;
  textarea.setAttribute("readonly", "");
  textarea.style.position = "fixed";
  textarea.style.opacity = "0";
  document.body.appendChild(textarea);
  textarea.select();
  const ok = document.execCommand("copy");
  textarea.remove();
  if (!ok) throw new Error("copy failed");
}
